/**
 * ThresholdSliders Component
 *
 * Two range sliders for configuring recommendation engine thresholds:
 * - Advanced advancement threshold (default 80%)
 * - Beginner threshold (default 50%)
 *
 * Reads initial values from skillsStore, updates local state on drag
 * for responsive UI, and persists on release via store action.
 *
 * Requirements: 2.1, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 4.1, 4.2, 4.3, 4.4
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSkillsStore } from '../../../store/skillsStore';
import { useUIStore } from '../../../store/uiStore';
import { ASSESSMENT_LEVEL_DISPLAY_MAP, DEFAULT_THRESHOLDS } from '../../../types/skills';

export function ThresholdSliders() {
  const thresholds = useSkillsStore(state => state.thresholds);
  const updateThresholds = useSkillsStore(state => state.updateThresholds);
  const showToast = useUIStore(state => state.showToast);

  // Local slider values for responsive UI
  const [masteryValue, setMasteryValue] = useState(
    thresholds?.mastery_threshold ?? DEFAULT_THRESHOLDS.mastery_threshold
  );
  const [niValue, setNiValue] = useState(
    thresholds?.needs_improvement_threshold ?? DEFAULT_THRESHOLDS.needs_improvement_threshold
  );

  // Track the last saved values for revert on error
  const savedMastery = useRef(masteryValue);
  const savedNi = useRef(niValue);

  // Sync local state when store thresholds change (e.g. after initial fetch)
  useEffect(() => {
    if (thresholds) {
      setMasteryValue(thresholds.mastery_threshold);
      setNiValue(thresholds.needs_improvement_threshold);
      savedMastery.current = thresholds.mastery_threshold;
      savedNi.current = thresholds.needs_improvement_threshold;
    }
  }, [thresholds]);

  /**
   * Persist current slider values to the backend.
   * On success: show success toast and update saved refs.
   * On error: show error toast and revert sliders to last saved values.
   */
  const persistThresholds = useCallback(async (mastery: number, ni: number) => {
    try {
      await updateThresholds({
        mastery_threshold: mastery,
        needs_improvement_threshold: ni,
      });
      savedMastery.current = mastery;
      savedNi.current = ni;
      showToast('success', 'Thresholds updated successfully');
    } catch {
      showToast('error', 'Failed to update thresholds');
      // Revert to last saved values
      setMasteryValue(savedMastery.current);
      setNiValue(savedNi.current);
    }
  }, [updateThresholds, showToast]);

  const handleMasteryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMasteryValue(Number(e.target.value));
  };

  const handleNiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNiValue(Number(e.target.value));
  };

  const handleMasteryCommit = () => {
    persistThresholds(masteryValue, niValue);
  };

  const handleNiCommit = () => {
    persistThresholds(masteryValue, niValue);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Recommendation Thresholds</h3>

      {/* Mastery threshold slider */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="mastery-threshold-slider" className="text-sm text-gray-600">
            {ASSESSMENT_LEVEL_DISPLAY_MAP['Advanced']} advancement threshold
          </label>
          <span className="text-sm font-medium text-gray-800" data-testid="mastery-value">
            {masteryValue}%
          </span>
        </div>
        <input
          id="mastery-threshold-slider"
          type="range"
          min={1}
          max={100}
          step={1}
          value={masteryValue}
          onChange={handleMasteryChange}
          onMouseUp={handleMasteryCommit}
          onTouchEnd={handleMasteryCommit}
          aria-valuemin={1}
          aria-valuemax={100}
          aria-valuenow={masteryValue}
          aria-valuetext={`${masteryValue}%`}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <p className="text-xs text-gray-500 mt-1">
          Percentage of skills at "{ASSESSMENT_LEVEL_DISPLAY_MAP['Advanced']}" level required to recommend advancement to the next group.
        </p>
      </div>

      {/* Needs improvement threshold slider */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="ni-threshold-slider" className="text-sm text-gray-600">
            {ASSESSMENT_LEVEL_DISPLAY_MAP['Beginner']} threshold
          </label>
          <span className="text-sm font-medium text-gray-800" data-testid="ni-value">
            {niValue}%
          </span>
        </div>
        <input
          id="ni-threshold-slider"
          type="range"
          min={1}
          max={100}
          step={1}
          value={niValue}
          onChange={handleNiChange}
          onMouseUp={handleNiCommit}
          onTouchEnd={handleNiCommit}
          aria-valuemin={1}
          aria-valuemax={100}
          aria-valuenow={niValue}
          aria-valuetext={`${niValue}%`}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <p className="text-xs text-gray-500 mt-1">
          Percentage of skills at "{ASSESSMENT_LEVEL_DISPLAY_MAP['Beginner']}" level that triggers a "Needs more time" recommendation.
        </p>
      </div>

      {/* Visual threshold bar */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-2">Recommendation zones</p>
        <div
          className="flex rounded-lg overflow-hidden h-8 text-xs font-medium"
          role="img"
          aria-label={`Recommendation zones: Needs More Time 0–${niValue}%, On Level ${niValue}–${masteryValue}%, Next Level ${masteryValue}–100%`}
        >
          <div
            className="flex items-center justify-center text-white transition-all duration-150"
            style={{ width: `${niValue}%`, backgroundColor: '#ef4444' }}
          >
            {niValue >= 15 && <span>Needs More Time</span>}
          </div>
          <div
            className="flex items-center justify-center text-white transition-all duration-150"
            style={{ width: `${Math.max(masteryValue - niValue, 0)}%`, backgroundColor: '#f59e0b' }}
          >
            {masteryValue - niValue >= 12 && <span>On Level</span>}
          </div>
          <div
            className="flex items-center justify-center text-white transition-all duration-150"
            style={{ width: `${100 - masteryValue}%`, backgroundColor: '#22c55e' }}
          >
            {100 - masteryValue >= 12 && <span>Next Level</span>}
          </div>
        </div>
        <div className="relative text-[10px] font-medium mt-1 h-4">
          <span className="absolute left-0" style={{ color: '#ef4444' }}>0%</span>
          <span className="absolute -translate-x-1/2" style={{ left: `${niValue}%`, color: '#ef4444' }}>{niValue}%</span>
          <span className="absolute -translate-x-1/2" style={{ left: `${masteryValue}%`, color: '#f59e0b' }}>{masteryValue}%</span>
          <span className="absolute right-0" style={{ color: '#22c55e' }}>100%</span>
        </div>
      </div>
    </div>
  );
}

export default ThresholdSliders;
